import EventEmitter from "./EventEmitter.js";
import Experience from "../Experience.js";
import * as THREE from "three";

/**
 * Manages time-related updates and events for the application.
 * Provides elapsed time, delta time, and a custom event loop using `requestAnimationFrame`.
 * @extends EventEmitter
 * @category Experience
 * @subcategory Utils
 */
export default class Time extends EventEmitter {
  constructor() {
    super();

    /**
     * Reference to the main Experience instance.
     * @type {Experience}
     */
    this.experience = new Experience();

    /**
     * The main THREE.js scene.
     * @type {THREE.Scene}
     */
    this.scene = this.experience.scene;

    /**
     * The canvas element used for rendering.
     * @type {HTMLCanvasElement}
     */
    this.canvas = this.experience.canvas;

    /**
     * Manages resources (e.g., textures, models) for the application.
     * @type {Object}
     */
    this.resources = this.experience.resources;

    /**
     * Time management utility from the Experience instance.
     * @type {Object}
     */
    this.time = this.experience.time;

    /**
     * Debugging tools for the application.
     * @type {Object}
     */
    this.debug = this.experience.debug;

    /**
     * Manages canvas sizes and dimensions.
     * @type {Object}
     */
    this.sizes = this.experience.sizes;

    /**
     * The main camera instance.
     * @type {Object}
     */
    this.camera = this.experience.camera;
    // Setup

    /**
     * The timestamp at the start of the application.
     * @type {number}
     */
    this.start = Date.now();

    /**
     * The current timestamp.
     * @type {number}
     */
    this.current = this.start;

    /**
     * The elapsed time since the application started (in milliseconds).
     * @type {number}
     */
    this.elapsed = 0;

    /**
     * The time difference between the current and the previous frame (in milliseconds).
     * @type {number}
     */
    this.delta = 16;

    /**
     * Mouse position vector.
     * @type {THREE.Vector2}
     */
    this.mouse = new THREE.Vector2();

    // Start the animation loop
    window.requestAnimationFrame(() => {
      this.tick();
    });
  }

  cleanup() {
    this.trigger = () => {}; // Prevent further event emissions
}

  /**
   * Animation loop that calculates time deltas and triggers a "tick" event.
   * Continuously updates `elapsed` and `delta` values.
   */
  tick() {
    const currentTime = Date.now();

    // Calculate time difference and update values
    this.delta = currentTime - this.current;
    this.current = currentTime;
    this.elapsed = this.current - this.start;

    // Emit the "tick" event for subscribers
    this.trigger("tick");

    // Request the next animation frame
    window.requestAnimationFrame(() => {
      this.tick();
    });
  }
}
